/**
 * LEOS Mission Ready - Dynamic Satellite Loader
 * Loads satellites from API while preserving featured satellites
 */

import * as BABYLON from '@babylonjs/core';
import { EARTH_SCALE } from './constants.js';
import { generateRealTimeTelemetry, toBabylonPosition } from './orbital-mechanics.js';
import { createSatellites } from './satellites.js';
import { apiClient } from './mission-ready/api-integration.js';

// Featured satellites that always show
const FEATURED_SATELLITES = {
    'CRTS1': {
        name: 'CyberRTS-1',
        modelPath: 'assets/crts_satellite.glb',
        noradId: '99901',
        featured: true,
        orbitalElements: {
            epoch: new Date('2024-01-01T00:00:00Z'),
            inclination: 51.6,
            raan: 100,
            eccentricity: 0.0007,
            argOfPerigee: 45,
            meanAnomaly: 0,
            meanMotion: 15.54
        }
    },
    'BULLDOG': {
        name: 'BULLDOG',
        modelPath: 'assets/bulldog_sat.glb',
        noradId: '99902',
        featured: true,
        orbitalElements: {
            epoch: new Date('2024-01-01T00:00:00Z'),
            inclination: 98.2,
            raan: 120,
            eccentricity: 0.0001,
            argOfPerigee: 90,
            meanAnomaly: 180,
            meanMotion: 14.8
        }
    }
};

// Generic satellite model for dynamic satellites
const GENERIC_SATELLITE_MODEL = 'assets/generic_satellite.glb';

/**
 * Load satellites from API and merge with featured satellites
 */
export async function loadDynamicSatellites() {
    const satellites = {};
    const orbitalElements = {};
    
    // 1. Add featured satellites first
    for (const [key, satellite] of Object.entries(FEATURED_SATELLITES)) {
        satellites[key] = {
            name: satellite.name,
            model: satellite.modelPath,
            noradId: satellite.noradId,
            featured: true
        };
        orbitalElements[key] = satellite.orbitalElements;
    }
    
    // 2. Try to load additional satellites from API
    try {
        console.log('Loading satellites from API...');
        const response = await apiClient.getSatellites();
        
        if (response && response.satellites) {
            console.log(`Loaded ${response.satellites.length} satellites from API`);
            
            // Add each API satellite
            response.satellites.forEach((sat, index) => {
                // Skip if it's a featured satellite
                if (sat.noradId === '99901' || sat.noradId === '99902') {
                    return;
                }
                
                const key = `SAT_${sat.id || index}`;
                satellites[key] = {
                    name: sat.name,
                    model: sat.model || GENERIC_SATELLITE_MODEL,
                    noradId: sat.noradId,
                    id: sat.id,
                    featured: false
                };
                
                // Use orbital elements from API or generate defaults
                if (sat.orbitalElements) {
                    orbitalElements[key] = {
                        epoch: new Date(sat.orbitalElements.epoch || Date.now()),
                        inclination: sat.orbitalElements.inclination || 45,
                        raan: sat.orbitalElements.raan || Math.random() * 360,
                        eccentricity: sat.orbitalElements.eccentricity || 0.001,
                        argOfPerigee: sat.orbitalElements.argOfPerigee || 0,
                        meanAnomaly: sat.orbitalElements.meanAnomaly || Math.random() * 360,
                        meanMotion: sat.orbitalElements.meanMotion || 15.0
                    };
                } else {
                    // Generate default orbital elements
                    orbitalElements[key] = generateDefaultOrbitalElements(index);
                }
            });
        }
    } catch (error) {
        console.warn('Failed to load satellites from API, using featured only:', error);
    }
    
    return { satellites, orbitalElements };
}

/**
 * Generate default orbital elements for satellites without data
 */
function generateDefaultOrbitalElements(index) {
    return {
        epoch: new Date(),
        inclination: 45 + (index * 10) % 90, // Vary inclination
        raan: (index * 30) % 360, // Spread around Earth
        eccentricity: 0.001,
        argOfPerigee: 0,
        meanAnomaly: (index * 45) % 360, // Different starting positions
        meanMotion: 14.5 + (Math.random() * 2) // LEO satellites
    };
}

/**
 * Create a generic satellite mesh for non-featured satellites
 */
export async function createGenericSatellite(scene, name) {
    // Create a simple satellite shape
    const body = BABYLON.MeshBuilder.CreateBox(name + "_body", {
        width: 2,
        height: 1,
        depth: 1
    }, scene);
    
    // Add solar panels
    const panel1 = BABYLON.MeshBuilder.CreateBox(name + "_panel1", {
        width: 4,
        height: 0.1,
        depth: 2
    }, scene);
    panel1.position.x = 3;
    panel1.parent = body;
    
    const panel2 = BABYLON.MeshBuilder.CreateBox(name + "_panel2", {
        width: 4,
        height: 0.1,
        depth: 2
    }, scene);
    panel2.position.x = -3;
    panel2.parent = body;
    
    // Create materials
    const bodyMat = new BABYLON.StandardMaterial(name + "_bodyMat", scene);
    bodyMat.diffuseColor = new BABYLON.Color3(0.7, 0.7, 0.8);
    bodyMat.specularColor = new BABYLON.Color3(0.3, 0.3, 0.3);
    body.material = bodyMat;
    
    const panelMat = new BABYLON.StandardMaterial(name + "_panelMat", scene);
    panelMat.diffuseColor = new BABYLON.Color3(0.1, 0.1, 0.3);
    panelMat.specularColor = new BABYLON.Color3(0.2, 0.2, 0.5);
    panelMat.emissiveColor = new BABYLON.Color3(0.05, 0.05, 0.1);
    panel1.material = panelMat;
    panel2.material = panelMat;
    
    // Scale to appropriate size
    body.scaling = new BABYLON.Vector3(0.002, 0.002, 0.002);
    
    return body;
}

/**
 * Enhanced createSatellites that loads from API
 */
export async function createDynamicSatellites(scene, activeSatellite, advancedTexture, simulationTime) {
    // Load satellites from API
    const { satellites, orbitalElements } = await loadDynamicSatellites();
    
    // Create satellites using the existing createSatellites function
    return createSatellites(scene, satellites, orbitalElements, activeSatellite, advancedTexture, simulationTime);
}

/**
 * Upload TLE data to create new satellites
 */
export async function uploadTLEData(tleData) {
    try {
        // Parse TLE data
        const satellites = parseTLEData(tleData);
        
        // Upload to API
        const response = await apiClient.callAPI('/satellites/import', {
            method: 'POST',
            body: JSON.stringify({ satellites })
        });
        
        console.log('TLE upload response:', response);
        return response;
    } catch (error) {
        console.error('Failed to upload TLE data:', error);
        throw error;
    }
}

/**
 * Parse TLE data into satellite objects
 */
function parseTLEData(tleText) {
    const lines = tleText.trim().split('\n');
    const satellites = [];
    
    // Process TLE data in groups of 3 lines
    for (let i = 0; i < lines.length; i += 3) {
        if (i + 2 < lines.length) {
            const name = lines[i].trim();
            const line1 = lines[i + 1].trim();
            const line2 = lines[i + 2].trim();
            
            // Basic TLE parsing
            if (line1.startsWith('1 ') && line2.startsWith('2 ')) {
                const noradId = line1.substring(2, 7).trim();
                const inclination = parseFloat(line2.substring(8, 16));
                const raan = parseFloat(line2.substring(17, 25));
                const eccentricity = parseFloat('0.' + line2.substring(26, 33));
                const argOfPerigee = parseFloat(line2.substring(34, 42));
                const meanAnomaly = parseFloat(line2.substring(43, 51));
                const meanMotion = parseFloat(line2.substring(52, 63));
                
                satellites.push({
                    name: name,
                    noradId: noradId,
                    orbitalElements: {
                        inclination,
                        raan,
                        eccentricity,
                        argOfPerigee,
                        meanAnomaly,
                        meanMotion,
                        epoch: new Date() // Would need to parse from TLE
                    }
                });
            }
        }
    }
    
    return satellites;
}

// Export for use in other modules
export { FEATURED_SATELLITES };